/**
 * Wire-format schema for `wp/v2/ezdish`.
 *
 * Same pattern as the auth schemas — parse the raw envelope from WP,
 * transform into our camelCase `Dish` entity. WP REST returns a *lot*
 * of extra fields per post (links, embedded relations, guid…); we
 * pick what we need and ignore the rest.
 *
 * Modifier-group wire shapes live alongside Dish because they're
 * embedded in every dish payload (`crb_modifier_group`) — they don't
 * have their own REST endpoint to be parsed elsewhere.
 *
 * Sibling files in this directory:
 *   - `categories.ts` — `wp/v2/ezcategory`
 *   - `tags.ts` — `wp/v2/eztag`
 *   - `dish-revisions.ts` — `wp/v2/ezdish/{id}/revisions`
 *   - `media.ts`, `pages.ts`, `auth.ts`, `shared-modifiers.ts` — others.
 */
import { z } from 'zod'

import { DishSchema, type Dish } from '@/entities/dish'
import type { ModifierGroup } from '@/entities/modifier'
import { htmlToPlain } from '@/shared/lib/html'

/** Inline 'featured' object some themes include alongside featured_media. */
const FeaturedInlineSchema = z
  .object({
    id: z.number().int().nonnegative().optional(),
    url: z.string().optional(),
  })
  .nullable()
  .optional()

/**
 * WP returns modifier groups in `crb_modifier_group` (Carbon Fields).
 * Each group has `_type` ('base' or 'dynamic'), name/desc, an optional
 * flat surcharge, and an inline `modifier` array.
 *
 * All string fields can be empty; we keep them as empty strings rather
 * than null so the UI doesn't have to branch on "missing vs blank".
 */
const ModifierWireSchema = z
  .object({
    _type: z.string().optional(),
    name: z.string().optional().default(''),
    price: z.union([z.string(), z.number()]).optional().default(''),
  })
  .transform((m) => ({ name: m.name, price: String(m.price ?? '') }))

const ModifierGroupWireSchema = z
  .object({
    _type: z.string().optional().default('base'),
    name: z.string().optional().default(''),
    desc: z.string().optional().default(''),
    group_price: z.union([z.string(), z.number()]).optional().default(''),
    modifier: z.array(ModifierWireSchema).optional().default([]),
  })
  .transform(
    (g): ModifierGroup => ({
      type: g._type === 'dynamic' ? 'dynamic' : 'base',
      name: g.name,
      desc: g.desc,
      groupPrice: String(g.group_price ?? ''),
      modifiers: g.modifier,
    }),
  )

const ModifierGroupListWire = z.array(ModifierGroupWireSchema).optional().default([])

const DishWireSchema = z
  .object({
    id: z.number().int().positive(),
    title: z.object({ rendered: z.string() }),
    content: z.object({ rendered: z.string() }),
    status: z.string(),
    crb_dish_price: z.union([z.string(), z.number()]).optional().default(''),
    crb_modifier_group: ModifierGroupListWire,
    ezcategory: z.array(z.number().int().nonnegative()).default([]),
    eztag: z.array(z.number().int().nonnegative()).default([]),
    ezmodgroup: z.array(z.number().int().nonnegative()).default([]),
    featured_media: z.number().int().nonnegative().nullable().default(null),
    featured: FeaturedInlineSchema,
    menu_order: z.number().int().optional().default(0),
    modified_gmt: z.string(),
  })
  .transform(
    (raw): Dish => ({
      id: raw.id,
      title: htmlToPlain(raw.title.rendered),
      description: htmlToPlain(raw.content.rendered),
      status: DishSchema.shape.status.parse(raw.status),
      price: String(raw.crb_dish_price ?? ''),
      categoryIds: raw.ezcategory,
      tagIds: raw.eztag,
      featuredMediaId: raw.featured_media,
      featuredImageUrl:
        raw.featured?.url && /^https?:\/\//.test(raw.featured.url) ? raw.featured.url : null,
      modifierGroups: raw.crb_modifier_group,
      sharedGroupIds: raw.ezmodgroup,
      menuOrder: raw.menu_order,
      // WP returns naive timestamp without "Z"; tag it as UTC.
      modifiedAt: raw.modified_gmt.endsWith('Z') ? raw.modified_gmt : `${raw.modified_gmt}Z`,
    }),
  )

export const dishWire = DishWireSchema
export const dishListWire = z.array(DishWireSchema)
